import { Box } from "@chakra-ui/react";
import { colors, customEvent, FUNERAL_SIDO_LIST, FUNERAL_SIGUNGU, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoParlorShareMapCard } from "./MubinsoMapPins";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import {
  CheckMarkIcon,
  MubinsoCtaButton,
  MubinsoGoiTalk,
  MubinsoKeepAll,
  MubinsoOrange as Orange,
  MubinsoReportPreviewCard,
  MubinsoReviewCarousel,
  MubinsoDetailListCard,
  MubinsoScreenLayout,
  MubinsoStepIntro as StepIntro,
} from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import { MUBINSO_E4_LEAD_MODAL_PROPS } from "./mubinsoE4Constants";
import { mubinsoEntryTrigger } from "./mubinsoConstants";

/** 진행 막대: D-1 · D-2 · 지역 선택 */
const TOTAL_STEPS = 3;

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  code: sido[0] as number,
  name: sido[1] as string,
}));

type ParlorFindE4Screen = "info1" | "info2" | "region" | "conversion";

/**
 * 무빈소 장례식장 찾기 퍼널 E4(신뢰 구간 개편). 현행 MubinsoParlorFindFunnel 의 처치군이다.
 *
 * D-1 무빈소가 안 되는 곳이 많다 → D-2 직접 알아보려면 → 지역 선택(시도 → 시군구) → 결과.
 * 결과 화면 제목 · 목록에 고른 지역 이름을 넣는다(Figma 「[서울 강남구]」 자리).
 *
 * 현행의 관심 장례식장 체크 실험(B안)은 붙이지 않는다. 그 실험은 Q2 응답 때 배정하는데 E4 에는 Q2 가 없고,
 * 두 실험을 섞지 않기 위해서다(MubinsoFunnelContainer 참고).
 *
 * 문구 · 순서는 Figma 「D퍼널 = 장례식장 소구」(21830:19742 안, 22523:19443 부터)가 기준이다(맞춤법만 고쳤다).
 * 지역 선택은 현행 화면(3열, 누르면 바로 다음)을 그대로 쓰고 제목 · 설명만 Figma 문구로 바꿨다.
 * 명세: wiki/plans/mubinso-funnel-trust.md
 */
export default function MubinsoParlorFindFunnelE4({
  topicAnswer,
  onComplete,
  onBack,
}: {
  /** D-1 의 「이전」. 퍼널 밖(E4 첫 화면)으로 돌아간다 */
  onBack: () => void;
  /** 첫 화면 버튼 답. E4 에는 Q2(상황)가 없어 상황은 결과 모달에서 받는다 */
  topicAnswer: string;
  onComplete: () => void;
}) {
  const [screen, setScreen] = useState<ParlorFindE4Screen>("info1");
  const [regionStep, setRegionStep] = useState<1 | 2>(1);
  const [sido, setSido] = useState<{ code: number; name: string } | null>(null);
  const [sigunguName, setSigunguName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  /** 지역 버튼을 누른 표시. 잠깐 보여 준 뒤 다음으로 넘어간다 */
  const [pickedRegion, setPickedRegion] = useState<string | null>(null);
  // 누른 표시 뒤 넘어가기 전(300ms)에 「이전」 · 이탈하면 넘어가지 않게 지운다
  const pickTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(pickTimer.current), []);

  /** 리드 메시지 · 결과 제목에 쓰는 "{시도} {시군구}" */
  const region = `${sido?.name ?? ""} ${sigunguName}`.trim();

  // 화면/지역단계 전환 시 스크롤 최상단(시도→시군구 포함)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen, regionStep]);

  // 퍼널별 단계 진행. 현행(parlorFind)과 단계 구성이 달라 funnel 값을 나눈다(전환은 mubinso_request_complete)
  useEffect(() => {
    const stepMap: Record<string, number> = { info1: 1, info2: 2, region: 3 };
    const step = stepMap[screen];
    if (step) {
      customEvent({
        newGtm: { action: "mubinso_funnel_step", properties: { funnel: "parlor_e4", step } },
      });
    }
  }, [screen]);

  const sigunguList = useMemo(() => {
    if (!sido) return [];
    return Array.from(FUNERAL_SIGUNGU.get(sido.code) ?? []).map((sigungu) => {
      const [code, name] = sigungu as [number, string];
      return { code, name };
    });
  }, [sido]);

  const prevEvent = (step: number) =>
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-prev-${step}` } });

  // D-1 무빈소가 안 되는 곳이 많다
  if (screen === "info1") {
    return (
      <MubinsoScreenLayout
        progress={{ current: 1, total: TOTAL_STEPS }}
        onPrev={() => {
          prevEvent(1);
          onBack();
        }}
        footer={
          <MubinsoCtaButton
            label="다음"
            onClick={() => {
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "funnel-next-1" },
              });
              setScreen("info2");
            }}
          />
        }
      >
        <StepIntro
          title={
            <>
              {/* 「무빈소 장례」 주황은 뺐다. 강조가 이 화면 요점(안 되는 곳이 많다)을 흐린다(주완 2026-10-30) */}
              무빈소 장례를 할 수 없는
              <br />
              장례식장이 많아요
            </>
          }
          desc={
            <>
              무빈소 장례는 장례식장 입장에서
              <br />
              수익이 생기지 않는 방식이기 때문이에요.
            </>
          }
        />
        <Spacing gap={24} />
        {/* B-3 지도 카드와 똑같이(주완 2026-10-30). D 고객은 B 퍼널을 거치지 않아 겹쳐 보이지 않는다 */}
        <MubinsoParlorShareMapCard />
        <Spacing gap={16} />
        {/* 예전 박스(「무빈소인데 장례식장에 비용을 내나요?」)는 C-1 과 같은 문장이고 이 화면 주제와 달라 B-3 박스로 바꿨다 */}
        <MubinsoGoiTalk>
          상황이 발생한 뒤에 급하게 찾으려면 <b>무빈소로 진행하지 못할 수도 있어요.</b> 그래서
          가능한 장례식장을 최대한 빠르게 확인하는 것이 좋아요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // D-2 직접 알아보려면
  if (screen === "info2") {
    return (
      <MubinsoScreenLayout
        progress={{ current: 2, total: TOTAL_STEPS }}
        onPrev={() => {
          prevEvent(2);
          setScreen("info1");
        }}
        footer={
          <MubinsoCtaButton
            label="다음"
            onClick={() => {
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "funnel-next-2" },
              });
              setRegionStep(1);
              setScreen("region");
            }}
          />
        }
      >
        <StepIntro
          // 360px 에서는 둘째 줄이 넘쳐 「달라져 / 요.」 로 끊기던 것을 낱말 단위로 넘긴다
          title={
            <MubinsoKeepAll>
              무빈소가 되는 곳도
              <br />
              <Orange>장례식장에 따라 비용</Orange>이 달라져요.
            </MubinsoKeepAll>
          }
          desc={
            <>
              같은 지역, 비슷한 장례식장 안에서도
              <br />
              안치실·입관실 사용료가 각각 달라요.
            </>
          }
        />
        <Spacing gap={24} />
        {/* 같은 구 안에서도 이용료가 다르다는 걸 실제 숫자로(주완 2026-10-30). 「직접 알아보려면」 목록은 아래 고이 박스가 맡아 뺐다.
            서울 강동구: 장례식장 5곳 중 고이가 무빈소 진행을 확인한 3곳의 무빈소 예상 비용
            (parlors_detail expected_charge_sum · mubinsoAvailableParlors, 2026-10-30). 이름은 A~C 로 가린다 */}
        {/* C-4 아이콘 목록 모양에 금액을 오른쪽에. 아이콘은 장례식장마다 다르게(지도 핀과 같은 건물 그림) */}
        <MubinsoDetailListCard
          title="서울 강동구 무빈소 장례식장 이용료"
          rows={[
            { icon: "icon_Graphic_Building", label: "A 장례식장", price: "58만 원" },
            { icon: "icon_Graphic_BuildingLow", label: "B 장례식장", price: "70만 원" },
            { icon: "icon_Graphic_BuildingAnnex", label: "C 장례식장", price: "103만 원" },
          ]}
        />
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          슬퍼할 시간도 부족한데, 장례식장을 찾는 데 많은 시간을 빼앗기시지 않도록,{" "}
          <b>지역을 알려주시면 고이가 장례식장과 비용을 안내</b>해드릴게요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // D-3 지역 선택(시도 → 시군구). 3열 버튼, 누른 표시 뒤 자동으로 다음
  if (screen === "region") {
    const items = regionStep === 1 ? SIDO_LIST : sigunguList;
    return (
      <MubinsoScreenLayout
        progress={{ current: 3, total: TOTAL_STEPS }}
        // 시군구 단계에선 시도를 잘못 골랐을 수 있어 시도로 돌아간다
        onPrev={() => {
          prevEvent(3);
          clearTimeout(pickTimer.current);
          setPickedRegion(null);
          if (regionStep === 2) setRegionStep(1);
          else setScreen("info2");
        }}
      >
        <StepIntro
          title={
            <>
              어느 지역에서 장례를
              <br />
              치르실 생각이신가요?
            </>
          }
          desc={
            <MubinsoKeepAll>
              고이가 선택하신 지역에서 무빈소 장례가
              <br />
              가능한 장례식장을 알려드릴게요.
            </MubinsoKeepAll>
          }
        />
        <Spacing gap={24} />
        {/* 3열 유지(2열은 서울 구 13줄로 길어진다). 16px 굵게 · 연회색 바탕으로 읽기 쉽게,
            누르면 주황 + 체크를 잠깐 보여 주고 자동으로 넘어간다. 이전 버튼이 있어 다음 버튼은 두지 않는다(주완 2026-10-30) */}
        <Box width="100%" display="grid" gridTemplateColumns="repeat(3, 1fr)" gridGap="10px">
          {items.map((item) => {
            const isPicked = pickedRegion === `${regionStep}-${item.name}`;
            return (
              <Box
                key={`${item.code}-${item.name}`}
                as="button"
                height="52px"
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="4px"
                bg={isPicked ? colors.orange650 : colors.warmGray100}
                color={isPicked ? colors.white : colors.gray900}
                className="body_1_normal_sb"
                cursor="pointer"
                _hover={isPicked ? undefined : { bg: colors.warmGray200 }}
                onClick={() => {
                  if (pickedRegion) return;
                  customEvent({
                    newGtm: { path: "mubinso_funnel", trigger_and_target: "region-answer" },
                  });
                  setPickedRegion(`${regionStep}-${item.name}`);
                  pickTimer.current = setTimeout(() => {
                    setPickedRegion(null);
                    if (regionStep === 1) {
                      setSido({ code: item.code, name: item.name });
                      setRegionStep(2);
                    } else {
                      setSigunguName(item.name);
                      setScreen("conversion");
                    }
                  }, 300);
                }}
              >
                {isPicked && <CheckMarkIcon color="currentColor" size={14} />}
                {item.name}
              </Box>
            );
          })}
        </Box>
      </MubinsoScreenLayout>
    );
  }

  // 결과
  return (
    <>
      <MubinsoConversionScreen
        entry="topic_parlor"
        onPrev={() => {
          // 결과의 「이전」은 시군구 선택으로 돌아간다(다른 구를 고를 수 있게)
          prevEvent(4);
          setRegionStep(2);
          setScreen("region");
        }}
        headline={
          <MubinsoKeepAll>
            {region}의 무빈소 장례식장,
            <br />
            고이가 찾아드릴게요
          </MubinsoKeepAll>
        }
        sub={
          <MubinsoKeepAll>
            무빈소 장례가 되는 곳을 찾아 알려드리고,
            <br />
            필요할 때 예약까지 대신해 드려요.
          </MubinsoKeepAll>
        }
        checkItems={[]}
        checkCard={
          <BaseStack width="100%" spacing={16}>
            {/* C 결과와 같은 「정리해서 알려드려요」 카드 + 후기, 고이 박스는 뺐다(주완 2026-10-30: 마지막 화면 모양 맞추기).
                신청하면 빈칸이 채워진다.
                「예약 대행」 줄은 뺐다(주완 2026-10-31): 카드는 알려드리는 정보만 두고, 고이가 해 주는 예약은 위 설명 글에서 말한다.
                「예약 가능한 날짜」 는 미리 알아보는 고객이라 날짜가 정해지지 않아 채울 수 없어 넣지 않았다 */}
            <MubinsoReportPreviewCard
              title="고이가 정리해서 알려드려요"
              rows={[
                {
                  icon: "icon_Graphic_Building",
                  label: "무빈소 되는 장례식장",
                  sub: `${sigunguName} 기준`,
                  value: "? 곳",
                },
                {
                  icon: "icon_Graphic_PriceList",
                  label: "장례식장별 이용료",
                  sub: "안치실 · 입관실",
                  value: "? 원",
                },
              ]}
            />
            {/* 장례식장을 찾아 연결 · 예약해 준 실제 후기(고이 후기 API sangjo_reviews 원문 문장을 그대로 잘라 썼다).
                무빈소 상품 후기만이 아니라 제목에 「무빈소」 를 넣지 않는다. 지도사 이름은 앞에서 잘랐다.
                운구 · 앰뷸런스 수배 후기는 섬뜩하다는 의견이 많아 뺐다(주완 2026-10-31). 같은 이유로 「시신 운구」 문장도 쓰지 않는다.
                이름은 운영 API 가 가려서 주는 값 그대로. 운영 후기 id: 1968 · 1576 · 2240 · 1565 */}
            <MubinsoReviewCarousel
              title="고이가 장례식장을 찾아드린 가족분들의 후기"
              onMove={(card, firstView) => {
                // 넘길 때마다 한 번(클릭 수) + 그 후기를 처음 볼 때 번호와 함께 한 번(몇 개까지 봤나)
                customEvent({
                  newGtm: { path: "mubinso_funnel", trigger_and_target: "fnd-review" },
                });
                if (firstView) {
                  customEvent({
                    newGtm: { path: "mubinso_funnel", trigger_and_target: `fnd-review-${card}` },
                  });
                }
              }}
              reviews={[
                {
                  text: (
                    <>
                      저희는 아버지 뜻에 따라 간소하게 진행 했고 <b>연결해주셨던 장례식장</b>,
                      추모공원도 너무 만족했습니다.
                    </>
                  ),
                  meta: "정OO 님 · 무빈소 · 2026.04",
                },
                {
                  text: (
                    <>
                      <b>장례식장 예약부터</b> 절차 안내까지 하나하나 세심하게 챙겨주셔서 큰 힘이
                      되었습니다.
                    </>
                  ),
                  meta: "김OO 님 · 가족장 · 2026.01",
                },
                {
                  text: (
                    <>
                      <b>장례식장부터</b> 시작해서 아무것도 정해지지 않은 상황에서 최대한
                      배려해주셔서 3일장중 해야될 일을 순서대로 잘 알아봐 주셨습니다.
                    </>
                  ),
                  meta: "정OO 님 · 가족장 · 2026.06",
                },
                {
                  text: (
                    <>
                      <b>신뢰할 수 있는 장례식장에 대한 추천</b>과 장례때 알아야 할 일까지.
                    </>
                  ),
                  meta: "원OO 님 · 가족장 · 2026.01",
                },
              ]}
            />
          </BaseStack>
        }
        primaryLabel="장례식장 정보 받아보기"
        onPrimary={() => {
          // 현행 찾기 퍼널과 같은 이름 + 진입별 개별 이벤트(fnd, A 도 같은 이름으로 쏜다)
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: mubinsoEntryTrigger("topic_parlor", "cta_main"),
            },
          });
          setIsModalOpen(true);
        }}
      />

      {/* Figma 에는 D 전용 팝업이 없다(결과 화면 아래에 동의 줄과 버튼만 있다). 번호를 받아야 해서
          C 팝업과 같은 틀에 결과 화면 버튼 문구를 쓴다 */}
      <MubinsoLeadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <MubinsoKeepAll>
            <Orange>장례식장 정보</Orange> 받아보기
          </MubinsoKeepAll>
        }
        description={
          <MubinsoKeepAll>
            고이가 2,000건 이상의 무빈소 장례 경험을 바탕으로, 필요하신 정보와 자료를 빠르게
            전해드릴게요.
          </MubinsoKeepAll>
        }
        buttonText="동의하고 장례식장 정보 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: "",
            topic: topicAnswer,
            region,
          })
        }
        {...MUBINSO_E4_LEAD_MODAL_PROPS}
        entry="topic_parlor"
        onSubmitted={onComplete}
      />
    </>
  );
}
